import LeftColumn from "@components/chat/left-column/LeftColumn";
import MiddleColumn from "@components/chat/middle-column/MiddleColumn";
import NavigationMenu from "@components/chat/navigation-menu/NavigationMenu";
import RightColumn from "@components/chat/right-column/RightColumn";
import DebugPanel from "@/components/dev/DebugPanel";
import Call from "@/components/chat/middle-column/call/Call";
import LoadingScreen from "@/components/shared/LoadingScreen";
import DbRecoveryScreen from "@/components/shared/DbRecoveryScreen";
import EmeraldNoteDialog from "@/components/shared/EmeraldNoteDialog/EmeraldNoteDialog";
import ReAddHomeScreenHint from "@/components/shared/ReAddHomeScreenHint/ReAddHomeScreenHint";
import { initLocalDB, isLocalDbOpenError } from "@/indexedDB/db";
import { loadChats } from "@/middleware/chats";
import { featureFlags } from "@/config/featureFlags";
import { useChatListState } from "@/zustand/ChatListState";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSocket } from "@/socketIO/useSocket";
import { useSync } from "@/hooks/useSync";
import { consumeNeedsPostRegistrationSync } from "@/middleware/auth";
import { syncChats } from "@/middleware/chatsSync";
import { runSync } from "@/indexedDB/services/syncEngine";
import { withSyncLock } from "@/indexedDB/services/syncLock";
import { CHAT_SYNC_FULL_TAKE } from "@/config/constants";
import { toast } from "@/hooks/useToast";
import { logoutUser } from "@/middleware/auth";
import { useTranslation } from "react-i18next";
import { runStorageMaintenance } from "@/lib/storage";
import { useSubscriptionRefresh } from "@/hooks/useSubscriptionRefresh";
import { usePlanRestrictionToast } from "@/hooks/usePlanRestrictionToast";
import PastDueBanner from "@/components/shared/PastDueBanner";
import { fetchSubscription } from "@/server/services/stripeService";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import { usePrefetchProgressBridge } from "@/middleware/hooks/attachments/usePrefetchProgressBridge";
import BfnCalculatorView from "@/components/chat/bfn/BfnCalculatorView";
import { BFN_AVAILABLE } from "@/config/bfnConfig";
import { useBfnCalculatorState } from "@/zustand/BfnCalculatorState";

export default function ChatPage() {
  useSocket();
  useSync(); // Phase 4: triggers sync on socket connect + tab focus
  useSubscriptionRefresh();
  usePlanRestrictionToast();
  usePrefetchProgressBridge(); // receiver-side chunked-download progress ring
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSubscription = useSubscriptionState((s) => s.setSubscription);

  // Handle Stripe portal return: silently refresh subscription state
  useEffect(() => {
    if (searchParams.get("portal_return") !== "true") {
      return;
    }
    // Clear the query param immediately so it doesn't persist on refresh
    navigate({ search: "" }, { replace: true });

    fetchSubscription()
      .then((sub) => {
        setSubscription(sub);
        toast({
          description: t("subscription.subscriptionUpdated"),
          duration: 3000,
        });
      })
      .catch(() => {
        // Silently ignore — cached state remains valid
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishedLoadingFirstPageOfChatList = useChatListState(
    (state) => state.finishedLoadingFirstPageOfChatList,
  );

  const bfnCalculatorOpen = useBfnCalculatorState((state) => state.isOpen);
  const bfnCalculatorMounted = useBfnCalculatorState((state) => state.mounted);

  // The local DB could not be opened or auto-recovered at boot — render the
  // recovery screen instead of logging the user out (see the appInit catch).
  const [dbUnavailable, setDbUnavailable] = useState(false);

  useEffect(() => {
    let syncTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const appInit = async () => {
      try {
        await initLocalDB();
        runStorageMaintenance();
        if (featureFlags.useNewSync) {
          // New sync: let the app render immediately (chat list starts empty).
          // The useSync hook triggers runSync() on socket connect, which
          // populates chats in the background and sets this flag again.
          useChatListState
            .getState()
            .setFinishedLoadingFirstPageOfChatList(true);
        } else {
          // Legacy: fetch chats from Rust getEnrichedChats before rendering.
          await loadChats({ take: 50, skip: 0 });
        }

        // Newly registered users: the backend Baptizer adds them to onboarding
        // chats ~2 s after the registration token is issued. The initial chat
        // load above races ahead of the Baptizer and returns 0 chats. Schedule
        // a follow-up sync at 5 s so those chats appear without a manual page
        // refresh. This must run on BOTH sync paths — gating it on the legacy
        // branch left the new-sync path with the flag never consumed and the
        // onboarding chats missing until a manual refresh. For all other
        // sessions this is a no-op.
        if (consumeNeedsPostRegistrationSync()) {
          syncTimeoutId = setTimeout(() => {
            if (featureFlags.useNewSync) {
              withSyncLock(() => runSync()).catch(() => {});
            } else {
              syncChats({ take: CHAT_SYNC_FULL_TAKE, skip: 0 }).catch(() => {});
            }
          }, 5000);
        }
        // progressivelyLoadChats(); // TODO: Implement and enable when ready
      } catch (error) {
        // A local-DB open failure is NOT an auth problem: logging out can't
        // repair an unopenable on-disk file (clearLocalDB needs an open DB) and
        // just drops the session. initLocalDB already tried retry + a throttled
        // delete-and-recreate, so surface the actionable recovery screen instead.
        if (isLocalDbOpenError(error)) {
          setDbUnavailable(true);
          return;
        }
        logoutUser();
        toast({
          duration: 1000,
          description:
            error instanceof Error ? error.message : t("errorOccurred"),
          variant: "destructive",
        });
      }
    };

    appInit();

    return () => {
      clearTimeout(syncTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (dbUnavailable) {
    return <DbRecoveryScreen />;
  }

  if (!finishedLoadingFirstPageOfChatList) {
    return <LoadingScreen title={t("startingApp")} />;
  }

  return (
    <>
      <EmeraldNoteDialog />
      <ReAddHomeScreenHint />
      <PastDueBanner />
      <NavigationMenu />
      <LeftColumn />
      <MiddleColumn />
      <RightColumn />
      <DebugPanel />
      {/* TO DO => check if if better to move this to the middle column - header  */}
      <Call />
      {/* Keep-alive: the view stays MOUNTED (hidden) after a close on capable
          devices so reopening skips the ~7 s handshake + SPA reboot; the view
          itself idle-unmounts after a while hidden. See bfnKeepAlive.ts. */}
      {BFN_AVAILABLE && bfnCalculatorMounted && (
        <BfnCalculatorView hidden={!bfnCalculatorOpen} />
      )}
    </>
  );
}
